import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay } from 'further-ui/utils';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type ReconcilableTransaction = {
  id: string;
  splitFromTransactionId?: string;
  operationType: ActionOperationType;
  unreconciledAmountTcy: number;
  currencyCode: CurrencyCode;
  counterpartyLabel: string;
  ledgerDate: CalendarDay;
  actionRef: ActionRef;
};

export default function useSplitReconcilableTransactions(
  defaultReconcilableTransactions: Array<ReconcilableTransaction>,
  onSplit?: (splitFromTransactionId: string, newTransactionId: string) => void,
) {
  const disclosure = useDisclosure();
  const [transactionToSplit, setTransactionToSplit] =
    useState<ReconcilableTransaction | null>(null);
  const [reconcilableTransactions, setReconcilableTransactions] = useState<
    Array<ReconcilableTransaction>
  >(defaultReconcilableTransactions);

  const selectTransactionToSplit = (transactionId: string) => {
    const transaction = reconcilableTransactions.find(
      (transaction) => transaction.id === transactionId,
    );
    if (!transaction) return;

    setTransactionToSplit(transaction);
    disclosure.onOpen();
  };

  const splitSelectedTransaction = (amount: number) => {
    const sourceTransactionIndex = reconcilableTransactions.findIndex(
      (transaction) => transaction.id === transactionToSplit?.id,
    );
    if (sourceTransactionIndex < 0) return;

    const sourceTransaction = reconcilableTransactions[sourceTransactionIndex];

    if (amount <= 0 || amount > sourceTransaction.unreconciledAmountTcy) return;

    const newTransactionId = uuidv4();

    setReconcilableTransactions([
      ...reconcilableTransactions.slice(0, sourceTransactionIndex),
      {
        ...sourceTransaction,
        unreconciledAmountTcy: sourceTransaction.unreconciledAmountTcy - amount,
      },
      {
        ...sourceTransaction,
        id: newTransactionId,
        unreconciledAmountTcy: amount,
        splitFromTransactionId: sourceTransaction.id,
      },
      ...reconcilableTransactions.slice(sourceTransactionIndex + 1),
    ]);
    setTransactionToSplit(null);
    disclosure.onClose();
    onSplit?.(sourceTransaction.id, newTransactionId);
  };

  return {
    reconcilableTransactions,
    showSplitTransactionDialog: disclosure.isOpen,
    closeSplitTransactionDialog: disclosure.onClose,
    transactionToSplit,
    selectTransactionToSplit,
    splitSelectedTransaction,
  };
}
